import { createAsyncThunk } from "@reduxjs/toolkit";
import { CommonRootState } from "@common/types/common-root-state-type";
import { DBReminderThunks } from "./DBServiceThunks";
import { subscriptionPopupActions } from "./SubscriptionPopupReducer";
import { Reminder } from "@common-models/reminder";
import { isDBServiceError } from "@common/utils/feathers-error-utils";

interface CreateReminderParams {
    reminder: Reminder;
}

interface PatchReminderParams {
    reminder: Reminder;
    options?: { ignoreOriginalEntity: boolean };
}

// Create new reminder with CRM limit error handling
export const createReminderThunk = createAsyncThunk<
    Reminder,
    CreateReminderParams,
    { state: CommonRootState }
>(
    'reminders/createWithLimitCheck',
    async ({ reminder }, thunkApi) => {
        const dispatch = thunkApi.dispatch;

        const result = await dispatch(DBReminderThunks.create(reminder)).unwrap();

        // Check if the result is an error object
        if (isDBServiceError(result)) {
            console.error('[reminders-thunks] Server validation error:', result);

            // Extract error message from the data property
            const errorMessage = result.data.message || String(result);

            // Check if error is due to CRM limit reached
            if (errorMessage.includes('active reminders limit') || errorMessage.includes('reminders limit')) {
                dispatch(subscriptionPopupActions.openPopup({
                    feature: 'reminders',
                    message: errorMessage,
                    upgradeReason: 'reminders_limit'
                }));
                throw new Error(errorMessage);
            } else {
                // Generic error - re-throw
                throw new Error(errorMessage);
            }
        }

        // Success - return the created reminder
        return result as Reminder;
    }
);

// Patch existing reminder with CRM limit error handling
export const patchReminderThunk = createAsyncThunk<
    Reminder,
    PatchReminderParams,
    { state: CommonRootState }
>(
    'reminders/patchWithLimitCheck',
    async ({ reminder, options }, thunkApi) => {
        const dispatch = thunkApi.dispatch;

        const result = await dispatch(DBReminderThunks.patch({
            entity: reminder,
            options: options || { ignoreOriginalEntity: false }
        })).unwrap();

        // Check if the result is an error object
        if (isDBServiceError(result)) {
            console.error('[reminders-thunks] Server validation error:', result);

            // Extract error message from the data property
            const errorMessage = result.data.message || String(result);

            // Check if error is due to CRM limit reached
            if (errorMessage.includes('active reminders limit') || errorMessage.includes('reminders limit')) {
                dispatch(subscriptionPopupActions.openPopup({
                    feature: 'reminders',
                    message: errorMessage,
                    upgradeReason: 'reminders_limit'
                }));
                throw new Error(errorMessage);
            } else {
                // Generic error - re-throw
                throw new Error(errorMessage);
            }
        }

        // Success - return the patched reminder
        return result as Reminder;
    }
);
